import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import InventoryIcon from "@mui/icons-material/Inventory";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function AdminProducts() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["products", debouncedSearch, pagination.offset, pagination.limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch?.trim()) params.append("search", debouncedSearch.trim());
      params.append("limit", pagination.limit.toString());
      params.append("offset", pagination.offset.toString());
      return api.get(`/admin/products?${params.toString()}`);
    },
  });

  const products = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const loading = isLoading;

  const handlePageChange = (event, newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    setPagination((prev) => ({ ...prev, offset: newOffset, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0, page: 1 }));
  };

  const totalPages = Math.ceil(total / pagination.limit);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Products
                </Typography>
              </Breadcrumbs>
            </Box>
            <AdminPageHeader
              title="Products"
              description="View and manage all products available on the site"
              icon={<InventoryIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/dashboard/products/new")}
              >
                Add Product
              </Button>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message || "Failed to fetch products"}
              </Alert>
            )}

            <Card>
              <CardContent>
                {/* Top pagination controls */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of{" "}
                        {total} products
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Pagination
                        count={totalPages}
                        page={pagination.page}
                        onChange={handlePageChange}
                        disabled={loading}
                        renderItem={(item) => (
                          <PaginationItem
                            component={Link}
                            to={`/dashboard/products?page=${item.page}&limit=${pagination.limit}&search=${searchQuery}`}
                            {...item}
                          />
                        )}
                      />
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search products by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 200 }}>Name</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>Price</TableCell>
                        <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Created</TableCell>
                        <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Updated</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: pagination.limit }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : products.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              {searchQuery ? "No products found matching your search" : "No products found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        products.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {product.name || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                ${parseFloat(product.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" color="text.secondary">
                                {product.created_at
                                  ? new Date(product.created_at).toLocaleDateString()
                                  : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" color="text.secondary">
                                {product.updated_at
                                  ? new Date(product.updated_at).toLocaleDateString()
                                  : "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/products/${product.id}`)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Bottom pagination controls */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton height={20} width={180} />
                      <Skeleton height={40} width={120} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Skeleton height={32} width={80} borderRadius={1} />
                      <Skeleton height={20} width={150} />
                      <Skeleton height={32} width={60} borderRadius={1} />
                    </Box>
                  </Box>
                ) : total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of{" "}
                        {total} products
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Pagination
                        count={totalPages}
                        page={pagination.page}
                        onChange={handlePageChange}
                        disabled={loading}
                        renderItem={(item) => (
                          <PaginationItem
                            component={Link}
                            to={`/dashboard/products?page=${item.page}&limit=${pagination.limit}&search=${searchQuery}`}
                            {...item}
                          />
                        )}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
